import { Component, Input, OnInit } from '@angular/core';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { AssociationService } from '../../../../../../../services/association/association.service';
import {
  batchAll,
  BulkUploadFailure,
  CallbackResult,
  CallbackResultStatus,
  generateCsvDownload,
  getParameterCaseInsensitive,
  modalErrorMessage,
  normalizeBirthdayInput,
  sortBulkUploadFailures
} from '../../../../../../../utils';
import {
  parseSpreadsheet,
  SpreadsheetParseError
} from '../../../../../../../spreadsheet-import';
import { Association } from '../../../../../../../models/association';
import { AccountService } from '../../../../../../../services/account/account.service';
import { environment } from '../../../../../../../../environments/environment';


const MAX_FILE_SIZE = 2500000;
const FIRST_NAME_COL = 'First Name';
const LAST_NAME_COL = 'Last Name';
const BIRTHDAY_COL = 'Birthday (YYYY-MM-DD)';
const BIRTHDAY_COL_ALT = 'Birthday';
const EMAIL_COL = 'Email (Optional)';
const EMAIL_COL_ALT = 'Email';
const EMAIL_COL_ALT_2 = 'Email Address';
const MAX_DISPLAYED_FAILURES = 10;


class MemberToUpload {
  rowNumber: number;
  firstName: string;
  lastName: string;
  birthday: string;
  email: string | null;
}


@Component({
  selector: 'app-bulk-upload-association-member-modal',
  templateUrl: './bulk-upload-association-member-modal.component.html',
  styleUrls: ['./bulk-upload-association-member-modal.component.css']
})
export class BulkUploadAssociationMemberModalComponent implements OnInit {

  @Input() association: Association;
  @Input() savedCallback: (result: CallbackResult) => undefined;
  /** Refreshes the list behind the modal without emitting a result message. */
  @Input() reloadCallback: () => void;

  public membersToUpload: MemberToUpload[] = [];

  public parsingUpload = false;
  public loadingSubmit = false;

  public validationErrorMessage = '';
  public saveFailures: BulkUploadFailure[] = [];
  public numSaved = 0;

  public readonly maxDisplayedFailures = MAX_DISPLAYED_FAILURES;

  constructor(
    public accountService: AccountService,
    public activeModal: NgbActiveModal,
    private associationService: AssociationService
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().can_manage_members) {
      return;
    }
  }

  public onFileSelected(event) {
    this.parsingUpload = true;
    this.validationErrorMessage = '';
    this.saveFailures = [];
    this.membersToUpload = [];

    const target = event.target as HTMLInputElement;
    if (target.files && target.files.length > 0) {
      const selectedFile = target.files[0];
      if (selectedFile.size > MAX_FILE_SIZE) {
        this.validationErrorMessage = 'Please ensure the selected file is no larger than 2 MB.';
        this.parsingUpload = false;
      } else {
        target.files[0].arrayBuffer()
          .then(buf => this.parseFile(buf))
          // `parseFile` reports its own failures; this is the read itself
          // failing (the file moved or became unreadable after selection),
          // which would otherwise leave the modal blank and unexplained.
          .catch(() => {
            this.validationErrorMessage =
              'File could not be read. Please ensure this is a valid .csv or .xlsx file.';
          })
          .finally(() => { this.parsingUpload = false; });
      }
    }
  }

  public downloadTemplate() {
    generateCsvDownload(
      [[FIRST_NAME_COL, LAST_NAME_COL, BIRTHDAY_COL, EMAIL_COL]],
      'association_members_to_upload.csv'
    );
  }

  /**
   * Whether a valid set of inputs have been selected.
   * Used to determine whether the save button is clickable.
   */
  public validInputSelected() {
    return (
      !this.validationErrorMessage &&
      (this.membersToUpload.length > 0)
    );
  }

  public isLoading() {
    return this.parsingUpload || this.loadingSubmit;
  }

  public handleSave() {
    if (!this.validInputSelected() || this.loadingSubmit || this.parsingUpload) { return; }

    this.loadingSubmit = true;
    this.saveFailures = [];
    this.numSaved = 0;

    let batchSize = 5;
    if (environment.local) {
      batchSize = 1;
    }

    batchAll(
      batchSize,
      this.membersToUpload,
      (memberToUpload) => {
        return this.associationService
          .addAssociationMemberByNameAndBirthday(
            this.association.id,
            memberToUpload.firstName,
            memberToUpload.lastName,
            memberToUpload.birthday,
            memberToUpload.email
          )
          .then(() => {
            this.numSaved++;
          })
          // A rejected row must not abort the remaining batches: every member
          // the server will accept is uploaded, and the rows it refused are
          // reported together at the end so staff can correct just those.
          .catch(reason => {
            this.saveFailures.push({
              rowNumber: memberToUpload.rowNumber,
              name: memberToUpload.firstName + ' ' + memberToUpload.lastName,
              message: modalErrorMessage(
                reason,
                'You do not have permission to add this member.',
                'An unexpected error occurred.'
              )
            });
          });
      }
    )
      .then(() => {
        if (this.saveFailures.length > 0) {
          sortBulkUploadFailures(this.saveFailures);
          // Rows which did save are already in the list behind this modal, but
          // the modal stays open on the failures — so refresh now rather than
          // on close, which ESC and backdrop dismissal would bypass.
          if (this.numSaved > 0) {
            this.reloadCallback();
          }
          return;
        }

        this.savedCallback({
          text: 'Members uploaded successfully.',
          status: CallbackResultStatus.success
        });
        this.activeModal.close();
      })
      .finally(() => { this.loadingSubmit = false;});
  }

  private async parseFile(fileArrayBuf: ArrayBuffer) {
    try {
      await parseSpreadsheet(
        fileArrayBuf,
        [
          FIRST_NAME_COL,
          LAST_NAME_COL,
          [BIRTHDAY_COL, BIRTHDAY_COL_ALT],
        ],
        (rowIdx, rowItem) => {
          // The spreadsheet's own row number: 1-based, and past the header row.
          const rowNumber = rowIdx + 2;
          try {
            this.membersToUpload.push(
              this.parseRow(rowItem, rowNumber)
            );
          } catch (e) {
            throw new SpreadsheetParseError(
              'Row ' + rowNumber.toString() + ' is invalid: ' + e.message
            );
          }
        }
      )
    } catch (e) {
      this.validationErrorMessage = e.message;
      return;
    }

    if (this.membersToUpload.length === 0) {
      this.validationErrorMessage = 'No association members in spreadsheet.'
    }
  }

  private parseRow(wsItem: any, rowNumber: number) {
    const firstNameRaw = getParameterCaseInsensitive(wsItem, FIRST_NAME_COL);
    const lastNameRaw = getParameterCaseInsensitive(wsItem, LAST_NAME_COL);
    let birthdayRaw = getParameterCaseInsensitive(wsItem, BIRTHDAY_COL);
    let emailRaw = getParameterCaseInsensitive(wsItem, EMAIL_COL);

    if (!birthdayRaw) {
      birthdayRaw = getParameterCaseInsensitive(wsItem, BIRTHDAY_COL_ALT);
    }
    if (!emailRaw) {
      emailRaw = getParameterCaseInsensitive(wsItem, EMAIL_COL_ALT);
    }
    if (!emailRaw) {
      emailRaw = getParameterCaseInsensitive(wsItem, EMAIL_COL_ALT_2);
    }

    if (!firstNameRaw) {
      throw TypeError('Missing first name.');
    }
    if (!lastNameRaw) {
      throw TypeError('Missing last name.');
    }
    if (!birthdayRaw) {
      throw TypeError('Missing birthday.');
    }
    const m: MemberToUpload = {
      rowNumber,
      firstName: firstNameRaw.toString().trim(),
      lastName: lastNameRaw.toString().trim(),
      birthday: birthdayRaw.toString().trim(),
      email: emailRaw ? emailRaw.toString().trim() : null
    };

    if (m.firstName === '') {
      throw TypeError('Missing first name.');
    }
    if (m.lastName === '') {
      throw TypeError('Missing last name.');
    }
    if (m.email === '') {
      m.email = null;
    }

    m.birthday = normalizeBirthdayInput(m.birthday);

    return m;
  }

}
